// The admin's Drum loops card (#430, stage 1): add a loop to every kit picker
// with no redeploy. The browser does what a server would: decodes the file
// (a file the browser cannot decode is refused before anything is uploaded),
// fits it to whole beats at the tempo given, and hashes it. Migration 0015's
// constraints hold the row to the same shape and its policies to admins.

import { useMemo, useState } from 'react';
import { fitLoop, sha256Hex, suggestTempos } from '@/audio/loopFit';
import {
  decodeDuration,
  deleteDrumLoop,
  loopMime,
  uploadDrumLoop,
  useUploadedLoops,
  type LoopUpload,
} from '@/data/uploadedLoops';
import { NumberField } from '@/components/NumberField';

type Rights = LoopUpload['rights'];

const RIGHTS: { id: Rights; label: string }[] = [
  { id: 'owned', label: 'Our own recording' },
  { id: 'licensed', label: 'Licensed' },
  { id: 'public-domain', label: 'Public domain' },
  { id: 'review-required', label: 'Not cleared' },
];

interface Picked {
  file: File;
  duration: number;
  sha256: string;
}

export function DrumLoopsCard({
  decode = decodeDuration,
}: {
  /** How a file's length is read; the tests stand in for the browser's decoder. */
  decode?: (bytes: ArrayBuffer) => Promise<number>;
}) {
  const loops = useUploadedLoops();
  const [picked, setPicked] = useState<Picked | null>(null);
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('Uploaded');
  const [beatsPerBar, setBeatsPerBar] = useState(4);
  const [bpm, setBpm] = useState<number | null>(null);
  const [rights, setRights] = useState<Rights>('owned');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);

  const suggestions = useMemo(
    () => (picked ? suggestTempos(picked.duration, beatsPerBar) : []),
    [picked, beatsPerBar],
  );
  const fit = picked && bpm ? fitLoop(picked.duration, bpm) : null;
  const wholeBars = fit ? fit.beats % beatsPerBar === 0 : false;

  const pick = async (file: File | undefined) => {
    setError(null);
    setDone(null);
    setPicked(null);
    if (!file) return;
    if (!loopMime(file, file.name)) {
      setError('That file is not an audio type the kit can play.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Loops are limited to 10 MB.');
      return;
    }
    try {
      const bytes = await file.arrayBuffer();
      const [duration, sha256] = await Promise.all([decode(bytes), sha256Hex(bytes)]);
      if (!(duration > 0.5) || duration > 120) {
        setError('A loop runs between half a second and two minutes.');
        return;
      }
      setPicked({ file, duration, sha256 });
      setLabel((current) => current || file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '));
      const first = suggestTempos(duration, beatsPerBar)[0];
      setBpm(first ?? null);
    } catch {
      setError('The browser could not decode that file.');
    }
  };

  const upload = async () => {
    if (!picked || !fit || !wholeBars || !label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await uploadDrumLoop({
        file: picked.file,
        fileName: picked.file.name,
        label: label.trim(),
        category: category.trim() || 'Uploaded',
        bpm: fit.bpm,
        beats: fit.beats,
        beatsPerBar,
        seconds: fit.seconds,
        tempoVerified: fit.tempoVerified,
        sha256: picked.sha256,
        rights,
        rightsNote: note.trim(),
      });
      setDone(`${label.trim()} added.`);
      setPicked(null);
      setLabel('');
      setNote('');
      setBpm(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string, storagePath: string) => {
    setError(null);
    try {
      await deleteDrumLoop({ id, storagePath });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setArmed(null);
    }
  };

  return (
    <section className="admin-card" data-testid="admin-drum-loops">
      <h2>Drum loops</h2>
      {error && (
        <p className="home-load-error" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="muted" role="status" data-testid="admin-loop-done">
          {done}
        </p>
      )}
      <div className="admin-loop-form">
        <label className="admin-field">
          <span>File</span>
          <input
            type="file"
            accept="audio/*,.m4a,.mp3,.wav,.ogg,.flac"
            onChange={(e) => void pick(e.target.files?.[0])}
            data-testid="admin-loop-file"
          />
        </label>
        {picked && (
          <>
            <p className="muted" data-testid="admin-loop-length">
              {picked.duration.toFixed(2)} s
            </p>
            <label className="admin-field">
              <span>Name</span>
              <input
                className="text-input"
                value={label}
                maxLength={120}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="admin-loop-label"
              />
            </label>
            <label className="admin-field">
              <span>Category</span>
              <input
                className="text-input"
                value={category}
                maxLength={60}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="admin-loop-category"
              />
            </label>
            <label className="admin-field">
              <span>Beats per bar</span>
              <NumberField
                className="text-input admin-mint-count"
                value={beatsPerBar}
                min={1}
                max={12}
                onCommit={setBeatsPerBar}
                data-testid="admin-loop-bar"
              />
            </label>
            <div className="admin-field">
              <span>Tempo</span>
              <span className="admin-loop-tempo">
                <NumberField
                  className="text-input admin-mint-count"
                  value={bpm ?? 0}
                  min={30}
                  max={400}
                  onCommit={setBpm}
                  aria-label="Tempo"
                  data-testid="admin-loop-bpm"
                />
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`btn btn-sm${s === bpm ? ' active' : ''}`}
                    aria-pressed={s === bpm}
                    onClick={() => setBpm(s)}
                    data-testid={`admin-loop-bpm-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </span>
            </div>
            <p className="muted" data-testid="admin-loop-fit">
              {!fit
                ? 'Choose a tempo.'
                : !wholeBars
                  ? `${fit.beats} beats, not whole bars of ${beatsPerBar}.`
                  : `${fit.beats / beatsPerBar} bars${fit.tempoVerified ? '' : ', off the beat'}.`}
            </p>
            <label className="admin-field">
              <span>Rights</span>
              <select
                className="text-input"
                value={rights}
                onChange={(e) => setRights(e.target.value as Rights)}
                data-testid="admin-loop-rights"
              >
                {RIGHTS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field">
              <span>Note</span>
              <input
                className="text-input"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                data-testid="admin-loop-note"
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !fit || !wholeBars || !label.trim()}
              onClick={() => void upload()}
              data-testid="admin-loop-upload"
            >
              {busy ? 'Adding…' : 'Add loop'}
            </button>
          </>
        )}
      </div>
      {loops.length === 0 ? (
        <p className="muted">No uploaded loops yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Loop</th>
              <th>Tempo</th>
              <th>Bars</th>
              <th>Rights</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loops.map((loop) => (
              <tr key={loop.id}>
                <td>
                  {loop.label}
                  <span className="muted"> · {loop.category}</span>
                </td>
                <td>{loop.bpm}</td>
                <td>
                  {loop.beats / loop.beatsPerBar} × {loop.beatsPerBar}
                </td>
                <td>{RIGHTS.find((r) => r.id === loop.rights)?.label ?? loop.rights}</td>
                <td>
                  {armed === loop.id ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onBlur={() => setArmed(null)}
                      onClick={() => void remove(loop.id, loop.storagePath)}
                      data-testid="admin-loop-remove-confirm"
                    >
                      Remove?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label={`Remove ${loop.label}`}
                      onClick={() => setArmed(loop.id)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
